import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import Icon from '../../common/Icon/Icon';
import { adminFetch, ago, Dot } from './adminUtil';
import AdminOverview from './AdminOverview';
import AdminLookup from './AdminLookup';
import AdminReports from './AdminReports';
import AdminShops from './AdminShops';
import AdminCatalog from './AdminCatalog';
import AdminErrors from './AdminErrors';
import './Admin.css';

/**
 * Admin console. Opens on the overview: one health check per system and
 * a list of what needs a person, each with the button that fixes it.
 * The other sections are where those buttons lead: look up any account
 * or trade, reports, shops, the card catalog's nightly jobs, and the
 * server's own error log.
 *
 * The section and whatever is open in it live in the URL
 * (?tab=lookup&trade=TS-XXXX), so links from the overview land on the
 * exact thing, and reloading keeps your place.
 */

const SECTIONS = [
    { id: 'overview', label: 'Overview', icon: 'activity' },
    { id: 'lookup', label: 'Look up', icon: 'search' },
    { id: 'reports', label: 'Reports', icon: 'flag' },
    { id: 'shops', label: 'Shops', icon: 'store' },
    { id: 'catalog', label: 'Catalog', icon: 'layers' },
    { id: 'errors', label: 'Errors', icon: 'bug' },
];

function Admin() {
    const { user } = useUser();
    const [params, setParams] = useSearchParams();
    const tab = SECTIONS.some(s => s.id === params.get('tab')) ? params.get('tab') : 'overview';
    const [overview, setOverview] = useState(null);
    const [overviewError, setOverviewError] = useState(null);
    const isAdmin = (user?.access_role || user?.role) === 'admin';

    const loadOverview = useCallback(async () => {
        try {
            setOverview(await adminFetch('/api/admin/overview'));
            setOverviewError(null);
        } catch (e) {
            setOverviewError(e.message);
        }
    }, []);

    // A status board: keep it honest without anyone pressing refresh
    useEffect(() => {
        if (!isAdmin) return undefined;
        loadOverview();
        const id = setInterval(loadOverview, 30000);
        return () => clearInterval(id);
    }, [isAdmin, loadOverview]);

    // Every section switch is a URL change; extra keys open a record
    const go = useCallback((next, extra = {}) => {
        const p = new URLSearchParams();
        if (next !== 'overview') p.set('tab', next);
        Object.entries(extra).forEach(([k, v]) => v != null && p.set(k, v));
        setParams(p);
        window.scrollTo({ top: 0 });
    }, [setParams]);

    if (!user) return <LoginGate title="Admin" section="Console" message="Log in to access the admin console." />;
    if (!isAdmin) return <div className="adm-denied">Admins only.</div>;

    const h = overview?.health;
    const counts = {
        overview: overview?.attention?.length || 0,
        reports: overview?.attention?.find(a => a.id === 'reports')?.count || 0,
        errors: h?.errors?.newCount || 0,
    };
    const current = SECTIONS.find(s => s.id === tab);

    return (
        <div className="adm">
            <header className="page-section-head adm-head">
                <h1>Admin <span className="page-section-current">{current.label}</span></h1>
                {h && (
                    <span className={`adm-host${h.isLive ? ' live' : ''}`} title={h.isLive
                        ? 'You are looking at the live server.'
                        : `This page is served by ${h.host}, not the live server (${h.liveHost}). The database is shared; disk, errors and job buttons are ${h.host}'s.`}>
                        <Dot state={h.isLive ? 'ok' : 'idle'} />
                        {h.host} <em>{h.isLive ? 'live' : 'dev'}</em>
                    </span>
                )}
            </header>

            <div className="adm-layout">
                <nav className="adm-rail" aria-label="Admin sections">
                    {SECTIONS.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            className={`adm-rail-item${tab === s.id ? ' active' : ''}`}
                            aria-current={tab === s.id ? 'page' : undefined}
                            onClick={() => go(s.id)}
                        >
                            <Icon name={s.icon} size={17} />
                            <span>{s.label}</span>
                            {counts[s.id] > 0 && (
                                <span className={`adm-rail-count${s.id === 'overview' && overview.attention.some(a => a.level === 'high') ? ' hot' : ''}`}>
                                    {counts[s.id]}
                                </span>
                            )}
                        </button>
                    ))}
                    {h && (
                        <p className="adm-rail-foot">
                            Checked {ago(h.checkedAt)}
                            <button type="button" onClick={loadOverview} title="Check again" aria-label="Check again">
                                <Icon name="refresh" size={13} />
                            </button>
                        </p>
                    )}
                </nav>

                <div className="adm-main">
                    {tab === 'overview' && (
                        <AdminOverview data={overview} error={overviewError} reload={loadOverview} go={go} />
                    )}
                    {tab === 'lookup' && (
                        <AdminLookup
                            params={params}
                            go={go}
                            onChanged={loadOverview}
                        />
                    )}
                    {tab === 'reports' && <AdminReports onChanged={loadOverview} go={go} />}
                    {tab === 'shops' && <AdminShops onChanged={loadOverview} go={go} />}
                    {tab === 'catalog' && <AdminCatalog onChanged={loadOverview} />}
                    {tab === 'errors' && <AdminErrors onChanged={loadOverview} />}
                </div>
            </div>
        </div>
    );
}

export default Admin;
